import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import { getFastMarketData, getPlotData } from "../../../services/user.service";
import Plotter from "./plotter";
import { dataByObkey } from "../../../common/extra";
import styles from "./styles.module.css";

registerLocale("es", es);

const criterion_colors = {
  Normal: styles.normal,
  SMC: styles.smc,
  FM: styles.fastMarket,
  Warning: styles.warning,
};

function FastMarketTable() {
  const [quotes, setQuotes] = useState(new Map());
  const [trades, setTrades] = useState(new Map());
  const [databyProd, setDatabyProd] = useState(new Map());
  const [startDate, setStartDate] = useState(new Date());

  const refresh = (mydate) => {
    let fmbyProds;
    let products;
    getFastMarketData(mydate)
      .then((res) => {
        fmbyProds = dataByObkey(res.data, "product");
        products = [...fmbyProds.keys()];

        if (products.length > 0) {
          getPlotData(mydate, products)
            .then((res) => {
              let rawdata = JSON.parse(res.data);
              let replacement = {
                ...(rawdata.replacement ?? {}),
                "GDAES_D+1": "GDAES_DA",
              };
              let nuquotes = dataByObkey(
                rawdata.quotes,
                "instrument",
                replacement
              );
              let nutrades = dataByObkey(
                rawdata.trades,
                "instrument",
                replacement
              );
              setDatabyProd(fmbyProds);
              setQuotes(nuquotes);
              setTrades(nutrades);
            })
            .catch((err) => console.log(err));
        } else {
          setDatabyProd(new Map());
          setQuotes(new Map());
          setTrades(new Map());
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    refresh(startDate);
    let interval;
    if (
      new Date(startDate).setHours(0, 0, 0, 0) ===
      new Date().setHours(0, 0, 0, 0)
    ) {
      interval = setInterval(() => {
        setStartDate(new Date());
      }, 60000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [startDate]);

  const handleDate = (date) => {
    setStartDate(date);
  };

  const columnNames = {
    Fecha: "dtime",
    "Instrumento negociado": "product",
    "Hora Inicial Criterio 1": "criterion1_start",
    "Hora Inicial Criterio 2": "criterion2_start",
    "Hora Final": "dtime",
    "Criterio 1": "criterion1",
    "Criterio 2": "criterion2",
    "Umbral Inferior": "threshold_lower",
    "Umbral Superior": "threshold_upper",
    "Valor Criterio 1": "criterion1_value",
    "Spread Inferior": "maxspread_q75",
    "Spread Superior": "maxspread_q90",
    "Valor Inf Criterio 2": "criterion2_lower_value",
    "Valor Sup Criterio 2": "criterion2_upper_value",
    "Criterio Instantáneo": "criterion",
  };

  const hours = [
    "Hora Inicial Criterio 1",
    "Hora Inicial Criterio 2",
    "Hora Final",
  ];
  const percent = ["Valor Inf Criterio 2", "Valor Sup Criterio 2"];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <h3>Fast Market</h3>
        </div>
        <div>
          <DatePicker
            locale="es"
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeInput
            timeCaption={null}
            selected={startDate}
            onChange={handleDate}
          />
        </div>
      </div>

      <div className={styles.container}>
        {databyProd.size === 0 ? (
          <div className={styles.noDataMessage}>
            <h5>No data for this period</h5>
          </div>
        ) : null}
        {[...databyProd.entries()].map(([prodkey, data]) => {
          return (
            <div className={styles.productSection} key={prodkey}>
              <div className={styles.productTitle}>
                <h4>{prodkey}</h4>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr key="fmheader" className={styles.tableHeader}>
                      {Object.keys(columnNames).map((key) => (
                        <th className={styles.tableHeaderCell} key={key}>
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, index) => (
                      <tr
                        key={"fmrow" + index}
                        className={
                          index % 2 === 0
                            ? styles.tableRow
                            : `${styles.tableRow} ${styles.tableRowAlt}`
                        }
                      >
                        {Object.keys(columnNames).map((colName) => {
                          if (colName === "Fecha") {
                            let date = new Date(row[columnNames[colName]]);
                            return (
                              <td className={styles.tableCell}>
                                {date.toLocaleString("es-ES", {
                                  year: "2-digit",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour12: false,
                                })}
                              </td>
                            );
                          } else if (hours.includes(colName)) {
                            let date = new Date(row[columnNames[colName]]);
                            return (
                              <td className={styles.tableCell}>
                                {date.toLocaleTimeString("es-ES", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                })}
                              </td>
                            );
                          } else if (percent.includes(colName)) {
                            return (
                              <td className={styles.tableCell}>
                                {row[columnNames[colName]]} %
                              </td>
                            );
                          } else if (colName === "Criterio Instantáneo") {
                            let val = row[columnNames[colName]];
                            let classname = styles.tableCell;
                            if (criterion_colors[val]) {
                              classname = criterion_colors[val];
                            }

                            return <td className={classname}>{val}</td>;
                          } else {
                            return (
                              <td className={styles.tableCell}>
                                {row[columnNames[colName]]}
                              </td>
                            );
                          }
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.plotterSection}>
                <Plotter
                  data={data}
                  quotes={quotes.get(prodkey) ?? []}
                  trades={trades.get(prodkey) ?? []}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FastMarketTable;
